//! Shared screen-space geometry for the frozen (pinned) panes.
//!
//! Both the pixi renderer (FrozenPanes) and the DOM overlay layer
//! (HTMLGridContainer) need the exact same pane rects and pinned transforms so
//! that DOM charts line up pixel-for-pixel with the pinned pixi cells. Keeping
//! the math in one place prevents the two from drifting.

import { sheets } from '@/app/grid/controller/Sheets';
import { content } from '@/app/gridGL/pixiApp/contentInstance';
import { pixiApp } from '@/app/gridGL/pixiApp/PixiApp';
import type { JsHtmlOutput } from '@/app/quadratic-core-types';

export interface FrozenLayout {
  hasRows: boolean;
  hasCols: boolean;

  // world->screen scale (uniform; no rotation)
  scale: number;

  // viewport screen translate (canvas-relative)
  vx: number;
  vy: number;

  headingWidth: number;
  headingHeight: number;

  // screen position where the pinned frozen content's world origin (0,0) is
  // drawn. At home this is the heading edge, but when the viewport drifts the
  // origin off the heading edge (e.g. while zooming out) the frozen content
  // must stay glued to where the body actually draws (0,0) — otherwise the
  // frozen strip and the body fall out of alignment. Clamped to the heading
  // edge so the frozen content never slides under the headings when the body
  // scrolls past the freeze line.
  originX: number;
  originY: number;

  // screen x/y of the freeze boundary; equals originX/originY when that axis is
  // not frozen
  freezeScreenX: number;
  freezeScreenY: number;

  screenW: number;
  screenH: number;

  // "torn" = the body has scrolled off its home position past the freeze line
  // on that axis, so the pinned panes show different content than the body
  // would. When nothing is torn, the frozen panes and body are contiguous and a
  // spanning chart can render as a single full element. A drift of the origin
  // off the heading edge (vx/vy > heading) is not a tear: the frozen content is
  // re-pinned to that origin and stays contiguous with the body.
  tornX: boolean;
  tornY: boolean;
  torn: boolean;
}

// how far (screen px) the viewport may sit from its home translate before the
// frozen panes are considered torn from the body
const TORN_EPSILON = 0.5;

// Returns the current frozen-pane screen geometry. Always returns a layout when
// a sheet exists (with hasRows/hasCols flags) so the body pane can use it even
// when nothing is frozen; returns undefined only when there's no sheet.
export const getFrozenLayout = (): FrozenLayout | undefined => {
  const sheet = sheets.sheet;
  if (!sheet) return undefined;

  const hasRows = sheet.frozenRows > 0;
  const hasCols = sheet.frozenColumns > 0;

  const viewport = pixiApp.viewport;
  const scale = viewport.scale.x;
  const headingWidth = content.headings.headingSize.width;
  const headingHeight = content.headings.headingSize.height;

  const boundary = sheet.getFrozenBoundary();

  const vx = viewport.x;
  const vy = viewport.y;

  // Pin the frozen content to wherever world (0,0) is actually drawn, but never
  // left of the heading edge. When the body scrolls past the freeze line the
  // origin would slide under the headings (vx/vy < heading), so we clamp to the
  // heading edge and let the body scroll under the pinned strip. When the origin
  // drifts the other way (vx/vy > heading, e.g. while zooming out) we follow it
  // so the frozen strip stays glued to the body's origin.
  const originX = Math.max(headingWidth, vx);
  const originY = Math.max(headingHeight, vy);

  const freezeScreenX = originX + boundary.x * scale;
  const freezeScreenY = originY + boundary.y * scale;

  // At home, world (0,0) maps to the heading edge, so the body is contiguous
  // with the frozen panes. Only a scroll past the freeze line (origin clamped to
  // the heading edge while the body slides under it) tears them apart; an origin
  // drift the other way is re-pinned above and stays contiguous. Headings start
  // at {0,0} before the first draw; comparing against that would mark the
  // viewport torn while it is still at the real home position.
  const headingsReady = headingWidth > 0 && headingHeight > 0;
  const tornX = headingsReady && hasCols && vx < headingWidth - TORN_EPSILON;
  const tornY = headingsReady && hasRows && vy < headingHeight - TORN_EPSILON;

  return {
    hasRows,
    hasCols,
    scale,
    vx,
    vy,
    headingWidth,
    headingHeight,
    originX,
    originY,
    freezeScreenX,
    freezeScreenY,
    screenW: viewport.screenWidth,
    screenH: viewport.screenHeight,
    tornX,
    tornY,
    torn: tornX || tornY,
  };
};

export type FrozenPane = 'body' | 'corner' | 'top' | 'left';

// True when the chart is anchored in the frozen region and extends past a freeze
// line (so part of it tears away when the body scrolls).
export const chartSpansFreezeBoundary = (data: JsHtmlOutput): boolean => {
  const sheet = sheets.getById(data.sheet_id);
  if (!sheet) return false;
  const fc = sheet.frozenColumns;
  const fr = sheet.frozenRows;
  if (fc === 0 && fr === 0) return false;

  const x = Number(data.x);
  const y = Number(data.y);
  const lastCol = x + (data.w ?? 1) - 1;
  const lastRow = y + (data.h ?? 1) - 1;

  const anchoredFrozen = (fc > 0 && x <= fc) || (fr > 0 && y <= fr);
  const extendsBody = (fc > 0 && x <= fc && lastCol > fc) || (fr > 0 && y <= fr && lastRow > fr);
  return anchoredFrozen && extendsBody;
};

// Which pane owns the cell at (x, y) based on its anchor relative to the frozen
// rows/columns. Columns/rows are 1-based and the first `frozen*` of them are
// pinned.
export const getFrozenPaneForCell = (x: number, y: number): FrozenPane => {
  const sheet = sheets.sheet;
  if (!sheet) return 'body';
  const inCols = sheet.frozenColumns > 0 && x <= sheet.frozenColumns;
  const inRows = sheet.frozenRows > 0 && y <= sheet.frozenRows;
  if (inCols && inRows) return 'corner';
  if (inCols) return 'left';
  if (inRows) return 'top';
  return 'body';
};

// The canvas-screen rect for a pane given the current layout. Returns undefined
// for a pane whose axis isn't frozen (so callers can hide it).
export const getPaneRect = (
  layout: FrozenLayout,
  pane: FrozenPane
): { x: number; y: number; width: number; height: number } | undefined => {
  const {
    headingWidth,
    headingHeight,
    freezeScreenX,
    freezeScreenY,
    screenW,
    screenH,
    hasRows,
    hasCols,
    tornX,
    tornY,
  } = layout;

  // a pane only exists for its frozen axis
  if (pane === 'corner' && (!hasRows || !hasCols)) return undefined;
  if (pane === 'top' && !hasRows) return undefined;
  if (pane === 'left' && !hasCols) return undefined;

  // Each pane clips at the freeze line on a TORN axis, but expands toward the
  // body on a contiguous (untorn) axis — so the single never-reparented iframe
  // keeps showing the whole strip that is still contiguous with the frozen pane
  // (e.g. scroll only horizontally and the full frozen-column strip stays live,
  // full height), and PNG copies replace only the torn-away slices.
  const rightEdge = tornX ? freezeScreenX : screenW;
  const bottomEdge = tornY ? freezeScreenY : screenH;

  switch (pane) {
    case 'corner':
      return {
        x: headingWidth,
        y: headingHeight,
        width: rightEdge - headingWidth,
        height: bottomEdge - headingHeight,
      };
    case 'top':
      return {
        x: freezeScreenX,
        y: headingHeight,
        width: screenW - freezeScreenX,
        height: bottomEdge - headingHeight,
      };
    case 'left':
      return {
        x: headingWidth,
        y: freezeScreenY,
        width: rightEdge - headingWidth,
        height: screenH - freezeScreenY,
      };
    case 'body':
      return {
        x: freezeScreenX,
        y: freezeScreenY,
        width: screenW - freezeScreenX,
        height: screenH - freezeScreenY,
      };
  }
};

// The canvas-screen translate (tx, ty) that maps world (0,0) into a pane,
// pinning the frozen axis/axes and scrolling the rest. Combined with `scale`
// this is the pane's world->screen transform.
export const getPaneTranslate = (layout: FrozenLayout, pane: FrozenPane): { tx: number; ty: number } => {
  const { originX, originY, vx, vy } = layout;
  switch (pane) {
    case 'corner':
      return { tx: originX, ty: originY };
    case 'top':
      return { tx: vx, ty: originY };
    case 'left':
      return { tx: originX, ty: vy };
    case 'body':
      return { tx: vx, ty: vy };
  }
};
